import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ThinTextField,
  ThinTextAreaField,
  AddButton,
  ThinDropdown,
  ThinDropdownWithSearch,
} from "../../../components/common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getGrantTypeConfig } from "../grantTypeConfig";

const VerificationSupport = ({
  grantId,
  onNext,
  isEditMode,
  showNextOnly,
  onStoreFormData,
  onValidationChange,
  initialData,
  onGrantUpdated,
  grantType = "sme",
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cfg = getGrantTypeConfig(grantType);
  const [loading, setLoading] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [touched, setTouched] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [verificationReportFile, setVerificationReportFile] = useState(null);
  const [spotCheckReportFile, setSpotCheckReportFile] = useState(null);

  const validationSchema = Yup.object({
    field_officer_name: Yup.string()
      .trim()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .min(2, "Field officer name must be at least 2 characters")
      .max(255, "Field officer name must not exceed 255 characters"),
    field_verification_date: Yup.date()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      )
      .nullable()
      .max(new Date(), "Field verification date cannot be in the future"),
    field_verification_district_id: Yup.string(),
    technical_assistance_provided: Yup.boolean().required(),
    verification_approved: Yup.boolean()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      )
      .nullable(),
    field_officer_remarks: Yup.string()
      .trim()
      .max(500, "Remarks must not exceed 500 characters")
      .when("technical_assistance_provided", {
        is: true,
        then: (schema) =>
          schema.required(
            "Remarks are required when technical assistance is provided"
          ),
        otherwise: (schema) => schema.nullable(),
      }),
  });

  const formik = useFormik({
    initialValues: {
      field_officer_name: initialData?.field_officer_name || "",
      field_verification_date: initialData?.field_verification_date || "",
      field_verification_district_id:
        initialData?.field_verification_district_id || "",
      technical_assistance_provided:
        initialData?.technical_assistance_provided !== undefined
          ? initialData.technical_assistance_provided
          : "",
      verification_approved:
        initialData?.verification_approved !== undefined
          ? initialData.verification_approved
          : "",
      field_officer_remarks: initialData?.field_officer_remarks || "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      await handleFormSubmit(values);
    },
  });

  useEffect(() => {
    if (initialData?.verification_report_file && !verificationReportFile) {
      setVerificationReportFile(initialData.verification_report_file);
    }
    if (initialData?.spot_check_report_file && !spotCheckReportFile) {
      setSpotCheckReportFile(initialData.spot_check_report_file);
    }
  }, [initialData]);

  const validateAndPickFile = (e) => {
    const file = e.target.files[0];
    if (!file) return null;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const maxSize = 1 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PDF or Word document");
      e.target.value = "";
      return null;
    }

    if (file.size > maxSize) {
      toast.error("File size should be less than 1MB");
      e.target.value = "";
      return null;
    }

    return file;
  };

  const handleFileChange = (e) => {
    const file = validateAndPickFile(e);
    if (file) {
      setVerificationReportFile(file);
      setTouched((prev) => ({ ...prev, verification_report_file: true }));
    }
  };

  const handleSpotCheckFileChange = (e) => {
    const file = validateAndPickFile(e);
    if (file) {
      setSpotCheckReportFile(file);
    }
  };

  const handleFormSubmit = async (values) => {
    setTouched((prev) => ({ ...prev, verification_report_file: true }));

    if (!validateFormWithErrors()) {
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        let putBody;
        if (
          verificationReportFile instanceof File ||
          spotCheckReportFile instanceof File
        ) {
          putBody = new FormData();
          Object.entries(values).forEach(([key, val]) => {
            if (val !== null && val !== undefined) {
              putBody.append(key, val);
            }
          });
          if (verificationReportFile instanceof File) {
            putBody.append("verification_report_file", verificationReportFile);
          }
          if (spotCheckReportFile instanceof File) {
            putBody.append("spot_check_report_file", spotCheckReportFile);
          }
        } else {
          putBody = { ...values };
        }

        const response = await fetchData(
          `${cfg.apiBase}/${grantId}`,
          "PUT",
          putBody,
          {},
          {},
          navigate,
          "",
          dispatch
        );

        if (response) {
          toast.success(
            "Verification & Support information updated successfully"
          );
          if (onGrantUpdated) {
            onGrantUpdated();
          }
        }
      } else {
        // For create mode, just store data locally
        if (onStoreFormData) {
          onStoreFormData({
            ...values,
            verification_report_file: verificationReportFile,
            spot_check_report_file: spotCheckReportFile,
          });
        }
        toast.success("Verification & Support details saved");
        if (onNext) {
          onNext();
        }
      }
    } catch (error) {
      console.error("Error saving verification & support details:", error);
      toast.error("Failed to save verification & support details");
    } finally {
      setLoading(false);
    }
  };

  // Enhanced validation function with detailed error tracking
  const validateFormWithErrors = () => {
    const errors = {};
    let isValid = true;

    // Validate field officer name (optional, but constrained if provided)
    if (
      formik.values.field_officer_name &&
      formik.values.field_officer_name.trim()
    ) {
      if (formik.values.field_officer_name.trim().length < 2) {
        errors.field_officer_name =
          "Field officer name must be at least 2 characters";
        isValid = false;
      } else if (formik.values.field_officer_name.trim().length > 255) {
        errors.field_officer_name =
          "Field officer name must not exceed 255 characters";
        isValid = false;
      }
    }

    // Validate field verification date (optional, but cannot be in the future)
    if (formik.values.field_verification_date) {
      const selectedDate = new Date(formik.values.field_verification_date);
      const today = new Date();
      today.setHours(23, 59, 59, 999); // Set to end of today

      if (selectedDate > today) {
        errors.field_verification_date =
          "Field verification date cannot be in the future";
        isValid = false;
      }
    }

    // Validate remarks based on technical assistance
    if (formik.values.technical_assistance_provided) {
      if (
        !formik.values.field_officer_remarks ||
        !formik.values.field_officer_remarks.trim()
      ) {
        errors.field_officer_remarks =
          "Remarks are required when technical assistance is provided";
        isValid = false;
      } else if (formik.values.field_officer_remarks.trim().length > 500) {
        errors.field_officer_remarks =
          "Remarks must not exceed 500 characters";
        isValid = false;
      }
    } else if (
      formik.values.field_officer_remarks &&
      formik.values.field_officer_remarks.length > 500
    ) {
      errors.field_officer_remarks = "Remarks must not exceed 500 characters";
      isValid = false;
    }

    // Verification report upload is mandatory
    if (!verificationReportFile) {
      errors.verification_report_file = "Verification report upload is required";
      isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  const validateFormQuiet = () => {
    // Check date validity when provided
    if (formik.values.field_verification_date) {
      const selectedDate = new Date(formik.values.field_verification_date);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (selectedDate > today) return false;
    }

    // Check field officer name length when provided
    if (formik.values.field_officer_name?.trim()) {
      if (formik.values.field_officer_name.trim().length < 2) return false;
      if (formik.values.field_officer_name.trim().length > 255) return false;
    }

    // Check remarks when technical assistance is provided
    if (formik.values.technical_assistance_provided) {
      if (!formik.values.field_officer_remarks?.trim()) return false;
      if (formik.values.field_officer_remarks.trim().length > 500)
        return false;
    } else if (
      formik.values.field_officer_remarks &&
      formik.values.field_officer_remarks.length > 500
    ) {
      return false;
    }

    if (!verificationReportFile) return false;

    return true;
  };

  // Validation with user feedback
  const validateForm = () => {
    const newTouched = {
      field_officer_name: true,
      field_verification_date: true,
      field_verification_district_id: true,
      field_officer_remarks: true,
    };
    setTouched(newTouched);

    const isValid = validateFormWithErrors();

    if (!isValid) {
      return false;
    }

    return true;
  };

  // Update validation state for parent component
  useEffect(() => {
    if (onValidationChange) {
      const isValid = validateFormQuiet();
      onValidationChange(isValid);
    }
  }, [formik.values, onValidationChange, verificationReportFile]);

  // Store form data whenever values change
  useEffect(() => {
    if (onStoreFormData) {
      onStoreFormData({
        ...formik.values,
        verification_report_file: verificationReportFile,
        spot_check_report_file: spotCheckReportFile,
      });
    }
  }, [formik.values, onStoreFormData, verificationReportFile, spotCheckReportFile]);

  useEffect(() => {
    if (Object.keys(touched).length > 0) {
      validateFormWithErrors();
    }
  }, [formik.values, touched, verificationReportFile]);

  // Handle field blur to mark as touched
  const handleBlur = (fieldName) => {
    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
    formik.handleBlur({ target: { name: fieldName } });
  };

  const handleFieldChange = (fieldName, value) => {
    formik.setFieldValue(fieldName, value);
    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
  };

  const getFieldError = (fieldName) => {
    return fieldErrors[fieldName] || formik.errors[fieldName];
  };

  const shouldShowError = (fieldName) => {
    return (
      (touched[fieldName] || formik.touched[fieldName]) &&
      getFieldError(fieldName)
    );
  };

  // Fetch districts on component mount
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const response = await fetchData(
          "districts",
          "GET",
          null,
          {},
          { status: 1 },
          navigate,
          "",
          dispatch
        );

        setDistricts(response?.data || []);
      } catch (error) {
        console.error("Error fetching districts:", error);
        toast.error("Failed to fetch districts");
      }
    };

    fetchDistricts();
  }, [navigate, dispatch]);

  // Handle next button click for create mode
  const handleNext = () => {
    setTouched((prev) => ({ ...prev, verification_report_file: true }));

    if (!verificationReportFile) {
      return;
    }

    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length > 0) {
        formik.setTouched(
          Object.keys(formik.values).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {})
        );
        return;
      }

      // Only store data locally, don't make API call
      if (onStoreFormData) {
        onStoreFormData({
          ...formik.values,
          verification_report_file: verificationReportFile,
          spot_check_report_file: spotCheckReportFile,
        });
      }
      toast.success("Verification & Support information saved");
      if (onNext) {
        onNext();
      }
    });
  };

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Field Officer Name */}
        <div>
          <ThinTextField
            label="Field Officer Name"
            name="field_officer_name"
            value={formik.values.field_officer_name}
            onChange={formik.handleChange}
            onBlur={() => handleBlur("field_officer_name")}
            error={shouldShowError("field_officer_name")}
            placeholder="Enter field officer name"
            isRequired={false}
          />
          {shouldShowError("field_officer_name") && (
            <div className="text-red-500 text-sm mt-1">
              {getFieldError("field_officer_name")}
            </div>
          )}
        </div>

        {/* Date of Field Verification */}
        <div>
          <ThinTextField
            label="Date of Field Verification"
            name="field_verification_date"
            type="date"
            value={formik.values.field_verification_date}
            onChange={formik.handleChange}
            onBlur={() => handleBlur("field_verification_date")}
            error={shouldShowError("field_verification_date")}
            isRequired={false}
          />
          {shouldShowError("field_verification_date") && (
            <div className="text-red-500 text-sm mt-1">
              {getFieldError("field_verification_date")}
            </div>
          )}
        </div>

        {/* District of Field Verification */}
        <div>
          <ThinDropdownWithSearch
            label="District of Field Verification"
            options={districts.map((d) => ({
              value: d.district_id || d.District_Id,
              label: d.district_name || d.District_Name,
            }))}
            value={formik.values.field_verification_district_id}
            onChange={(value) =>
              handleFieldChange("field_verification_district_id", value)
            }
            error={shouldShowError("field_verification_district_id")}
            placeholder="Select district"
            required={false}
          />
          {shouldShowError("field_verification_district_id") && (
            <div className="text-red-500 text-sm mt-1">
              {getFieldError("field_verification_district_id")}
            </div>
          )}
        </div>

        {/* Technical Assistance Provided */}
        <div>
          <ThinDropdown
            label="Technical Assistance Provided?"
            id="technical-assistance-dropdown"
            options={[
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ]}
            value={formik.values.technical_assistance_provided}
            onChange={(e) => {
              const value = e.target.value === "true";
              handleFieldChange("technical_assistance_provided", value);
              // Clear remarks if technical assistance is set to false
              if (!value) {
                handleFieldChange("field_officer_remarks", "");
              }
            }}
            name="technical_assistance_provided"
            error={shouldShowError("technical_assistance_provided")}
          />
          {shouldShowError("technical_assistance_provided") && (
            <div className="text-red-500 text-sm mt-1">
              {getFieldError("technical_assistance_provided")}
            </div>
          )}
        </div>

        {/* Approved */}
        <div>
          <ThinDropdown
            label="Approved"
            id="verification-approved-dropdown"
            options={[
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ]}
            value={formik.values.verification_approved}
            onChange={(e) => {
              const value = e.target.value === "true";
              handleFieldChange("verification_approved", value);
            }}
            name="verification_approved"
            error={shouldShowError("verification_approved")}
            required={false}
          />
          {shouldShowError("verification_approved") && (
            <div className="text-red-500 text-sm mt-1">
              {getFieldError("verification_approved")}
            </div>
          )}
        </div>

        {/* Verification Report Upload */}
        <div>
          <label className="text-[0.9rem] text-[#565656] mb-1 font-[InterLight] block">
            Verification Report <span className="text-red-500">*</span>
          </label>
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            className="text-[15px] font-[InterLight] text-[#565656] bg-[#ebeef2] focus:bg-[#dde2e9] py-2 px-4 outline-none border-[#dde2e9] border rounded-[0.35rem] w-full"
          />
          {verificationReportFile instanceof File && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
              <p className="text-sm text-green-800 flex items-center">
                <span className="mr-2">✓</span>
                Selected:{" "}
                <strong className="ml-1">{verificationReportFile.name}</strong>
              </p>
              <p className="text-xs text-green-600 mt-1">
                Size: {(verificationReportFile.size / (1024 * 1024)).toFixed(2)}{" "}
                MB
              </p>
            </div>
          )}
          {typeof verificationReportFile === "string" &&
            verificationReportFile && (
              <p className="text-sm text-blue-600 mt-1">
                Current file:{" "}
                <a
                  href={`${process.env.REACT_APP_BASE_URL}${verificationReportFile}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-800"
                >
                  View / Download
                </a>
              </p>
            )}
          <p className="text-xs text-gray-500 mt-1">
            Accepted formats: PDF, DOC, DOCX (Max size: 1MB)
          </p>
          {shouldShowError("verification_report_file") && (
            <div className="text-red-500 text-sm mt-1">
              {getFieldError("verification_report_file")}
            </div>
          )}
        </div>

        <div>
          <label className="text-[0.9rem] text-[#565656] mb-1 font-[InterLight] block">
            Spot Check Report
          </label>
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleSpotCheckFileChange}
            className="text-[15px] font-[InterLight] text-[#565656] bg-[#ebeef2] focus:bg-[#dde2e9] py-2 px-4 outline-none border-[#dde2e9] border rounded-[0.35rem] w-full"
          />
          {spotCheckReportFile instanceof File && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
              <p className="text-sm text-green-800 flex items-center">
                <span className="mr-2">✓</span>
                Selected:{" "}
                <strong className="ml-1">{spotCheckReportFile.name}</strong>
              </p>
              <p className="text-xs text-green-600 mt-1">
                Size: {(spotCheckReportFile.size / (1024 * 1024)).toFixed(2)}{" "}
                MB
              </p>
            </div>
          )}
          {typeof spotCheckReportFile === "string" &&
            spotCheckReportFile && (
              <p className="text-sm text-blue-600 mt-1">
                Current file:{" "}
                <a
                  href={`${process.env.REACT_APP_BASE_URL}${spotCheckReportFile}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-800"
                >
                  View / Download
                </a>
              </p>
            )}
          <p className="text-xs text-gray-500 mt-1">
            Accepted formats: PDF, DOC, DOCX (Max size: 1MB) — Optional
          </p>
        </div>

        {/* Remarks by Field Officer */}
        <div className="mt-1">
          <ThinTextAreaField
            label="Remarks by Field Officer"
            name="field_officer_remarks"
            value={formik.values.field_officer_remarks}
            onChange={formik.handleChange}
            onBlur={() => handleBlur("field_officer_remarks")}
            error={shouldShowError("field_officer_remarks")}
            placeholder="Enter field officer remarks..."
            rows={4}
            maxLength={500}
          />
          <div className="text-xs text-gray-500 mt-1 text-right">
            {(formik.values.field_officer_remarks || "").length} / 500 characters
          </div>
          {shouldShowError("field_officer_remarks") && (
            <div className="text-red-500 text-sm mt-1">
              {getFieldError("field_officer_remarks")}
            </div>
          )}
        </div>
      </div>

      <div className="pt-4 flex justify-end gap-3">
        {showNextOnly && !isEditMode && (
          <button
            type="button"
            onClick={handleNext}
            className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Saving..." : "Next"}
          </button>
        )}
        {isEditMode && (
          <AddButton
            text="Update"
            type="submit"
            isLoading={loading}
            disabled={loading}
          />
        )}
      </div>
    </form>
  );
};

export default VerificationSupport;
